import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebase-manager';
import { FaCheckCircle, FaSpinner, FaUser, FaEnvelope, FaPhone, FaBuilding, FaBriefcase, FaUsers, FaIndustry, FaCommentDots, FaCalendarAlt, FaMapMarkerAlt } from 'react-icons/fa';
import { useWebAppConfig } from '../context/webAppConfigContext';

interface BookDemoSectionProps {
  onTrackSubmit?: () => void;
}

const VENUE_TYPES = [
  'Bar / Nightclub',
  'Restaurant / Cafe',
  'University / Campus',
  'Conference / Trade Show',
  'Sporting Event / Arena',
  'Festival / Outdoor Event',
  'Coworking / Office',
  'Retail / Pop-up Shop',
  'Other',
];

const BookDemoSection: React.FC<BookDemoSectionProps> = ({ onTrackSubmit }) => {
  const { config } = useWebAppConfig();
  const bd = config.bookDemo;

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    jobTitle: '',
    companySize: '',
    venueType: '',
    industry: '',
    message: '',
  });
  
  const [loading, setLoading] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Add document to Firestore
      await addDoc(collection(db, 'demo-bookings'), {
        ...formData,
        createdAt: serverTimestamp(),
        status: 'pending',
        source: 'website'
      });

      // Show success modal
      onTrackSubmit?.();
      setShowThankYou(true);
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        jobTitle: '',
        companySize: '',
        venueType: '',
        industry: '',
        message: '',
      });
    } catch (err) {
      console.error('Error booking demo:', err);
      setError('Failed to submit your request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="z-20 flex-shrink-0 py-8">
      <div className="relative min-h-[350px] lg:h-[calc(100vh-8rem)]">
        {/* Background Giant C */}
        <div 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[30rem] opacity-25 pointer-events-none"
          style={{ 
            fontFamily: '"Anton", sans-serif',
            background: 'linear-gradient(135deg, #f97316 0%, #fbbf24 45%, #3b82f6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            filter: 'drop-shadow(0 20px 60px rgba(0,0,0,0.35))'
          }}
        >
          C
        </div>
        
        {/* Form Content */}
        <div className="relative h-full flex items-center justify-center lg:overflow-y-auto">
          <div className="bg-BLACK/90 backdrop-blur-xl rounded-3xl p-6 lg:p-12 w-full h-auto lg:max-h-full border border-WHITE/20 shadow-2xl">
            <div className="flex items-center justify-center gap-4 mb-3">
              <FaCalendarAlt className="text-ORANGE" size={40} />
              <h2 className="text-4xl lg:text-5xl font-bold text-WHITE">{bd.headline}</h2>
            </div>
            <p className="text-WHITE/80 text-lg mb-8 text-center">
              {bd.subheadline}
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Fields */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-WHITE text-sm font-medium mb-2 flex items-center gap-2">
                    <FaUser className="text-ORANGE" size={14} />
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-WHITE/10 border border-WHITE/20 rounded-lg text-WHITE placeholder-WHITE/50 focus:outline-none focus:border-ORANGE transition-colors"
                    placeholder="John"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-WHITE text-sm font-medium mb-2 flex items-center gap-2">
                    <FaUser className="text-ORANGE" size={14} />
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-WHITE/10 border border-WHITE/20 rounded-lg text-WHITE placeholder-WHITE/50 focus:outline-none focus:border-ORANGE transition-colors"
                    placeholder="Doe"
                  />
                </div>
              </div>

              {/* Contact Fields */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="email" className="block text-WHITE text-sm font-medium mb-2 flex items-center gap-2">
                    <FaEnvelope className="text-ORANGE" size={14} />
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-WHITE/10 border border-WHITE/20 rounded-lg text-WHITE placeholder-WHITE/50 focus:outline-none focus:border-ORANGE transition-colors"
                    placeholder="john@company.com"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-WHITE text-sm font-medium mb-2 flex items-center gap-2">
                    <FaPhone className="text-ORANGE" size={14} />
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-WHITE/10 border border-WHITE/20 rounded-lg text-WHITE placeholder-WHITE/50 focus:outline-none focus:border-ORANGE transition-colors"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              {/* Company Fields */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="company" className="block text-WHITE text-sm font-medium mb-2 flex items-center gap-2">
                    <FaBuilding className="text-ORANGE" size={14} />
                    Company *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-WHITE/10 border border-WHITE/20 rounded-lg text-WHITE placeholder-WHITE/50 focus:outline-none focus:border-ORANGE transition-colors"
                    placeholder="Company Name"
                  />
                </div>
                <div>
                  <label htmlFor="jobTitle" className="block text-WHITE text-sm font-medium mb-2 flex items-center gap-2">
                    <FaBriefcase className="text-ORANGE" size={14} />
                    Job Title *
                  </label>
                  <input
                    type="text"
                    id="jobTitle"
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-WHITE/10 border border-WHITE/20 rounded-lg text-WHITE placeholder-WHITE/50 focus:outline-none focus:border-ORANGE transition-colors"
                    placeholder="Marketing Director"
                  />
                </div>
              </div>

              {/* Additional Info */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="companySize" className="block text-WHITE text-sm font-medium mb-2 flex items-center gap-2">
                    <FaUsers className="text-ORANGE" size={14} />
                    Company Size *
                  </label>
                  <select
                    id="companySize"
                    name="companySize"
                    value={formData.companySize}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-WHITE/10 border border-WHITE/20 rounded-lg text-WHITE focus:outline-none focus:border-ORANGE transition-colors"
                  >
                    <option value="" className="bg-BLACK">Select size</option>
                    <option value="1-10" className="bg-BLACK">1-10 employees</option>
                    <option value="11-50" className="bg-BLACK">11-50 employees</option>
                    <option value="51-200" className="bg-BLACK">51-200 employees</option>
                    <option value="201-500" className="bg-BLACK">201-500 employees</option>
                    <option value="501-1000" className="bg-BLACK">501-1000 employees</option>
                    <option value="1000+" className="bg-BLACK">1000+ employees</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="venueType" className="block text-WHITE text-sm font-medium mb-2 flex items-center gap-2">
                    <FaMapMarkerAlt className="text-ORANGE" size={14} />
                    Venue Type *
                  </label>
                  <select
                    id="venueType"
                    name="venueType"
                    value={formData.venueType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-WHITE/10 border border-WHITE/20 rounded-lg text-WHITE focus:outline-none focus:border-ORANGE transition-colors"
                  >
                    <option value="" className="bg-BLACK">Select venue type</option>
                    {VENUE_TYPES.map(vt => (
                      <option key={vt} value={vt} className="bg-BLACK">{vt}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Industry */}
              <div>
                <label htmlFor="industry" className="block text-WHITE text-sm font-medium mb-2 flex items-center gap-2">
                  <FaIndustry className="text-ORANGE" size={14} />
                  Industry
                </label>
                <input
                  type="text"
                  id="industry"
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-WHITE/10 border border-WHITE/20 rounded-lg text-WHITE placeholder-WHITE/50 focus:outline-none focus:border-ORANGE transition-colors"
                  placeholder="Technology, Hospitality, Education, etc."
                />
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-WHITE text-sm font-medium mb-2 flex items-center gap-2">
                  <FaCommentDots className="text-ORANGE" size={14} />
                  Additional Information
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 bg-WHITE/10 border border-WHITE/20 rounded-lg text-WHITE placeholder-WHITE/50 focus:outline-none focus:border-ORANGE transition-colors resize-none"
                  placeholder="Tell us about your goals and what you'd like to learn..."
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-500/20 border border-red-500 rounded-lg p-4">
                  <p className="text-red-300 text-sm">{error}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-ORANGE text-WHITE px-8 py-4 rounded-full text-lg font-medium hover:bg-WHITE hover:text-BLACK transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                {loading ? (
                  <>
                    <FaSpinner className="animate-spin" size={20} />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <span>Request Your Demo</span>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Thank You Modal */}
      {showThankYou && (
        <div className="fixed inset-0 bg-BLACK/90 backdrop-blur-lg z-50 flex items-center justify-center p-4">
          <div className="bg-WHITE rounded-3xl p-8 lg:p-12 max-w-md w-full text-center animate-fade-in">
            <div className="flex justify-center mb-6">
              <FaCheckCircle className="text-green-500" size={64} />
            </div>
            <h3 className="text-3xl font-bold text-BLACK mb-4">Thank You!</h3>
            <p className="text-DARK_GREY text-lg mb-8">
              We've received your demo request. Our team will reach out to you within 24 hours to schedule your personalized demo.
            </p>
            <button
              onClick={() => setShowThankYou(false)}
              className="bg-ORANGE text-WHITE px-8 py-3 rounded-full text-lg font-medium hover:bg-BLACK transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookDemoSection;
